import './InvitesDialog.scss';

import * as React from 'react';

import {
  SlDropdown,
  SlMenu,
  SlMenuItem,
} from '@shoelace-style/shoelace/dist/react';
import { WorkspaceRole } from '~api/workspaces/getWorkspaceMembers';
import { isRoleAtOrBelow } from '~api/workspaces/roleMeetsFloor';

import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { IconChevron } from '~shared/components/icons/micro';
import { IconLock } from '~shared/components/icons/standard';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useGetWorkspaceCodeStatuses } from '~teacher/hook/workspaces/code';

import {
  INVITE_ROLE_ORDER,
  inviteRoleDescription,
  inviteRoleLabel,
} from './inviteRoleConfig';
import { InviteRolePanel } from './InviteRolePanel';

interface InvitesDialogProps {
  workspaceId: string;
  handleClose: () => void;
  isOpen: boolean;
}

function StatusBadge({ active }: { active: boolean }) {
  // normal-case drops the Badge's default uppercase.
  return active ? (
    <Badge tone="beta" textTransform="normal-case">
      {translate('Active')}
    </Badge>
  ) : (
    <Badge tone="muted" textTransform="normal-case">
      {translate('No invite')}
    </Badge>
  );
}

export function InvitesDialog({
  workspaceId,
  handleClose,
  isOpen,
}: InvitesDialogProps) {
  const { roleQuery } = useWorkspacePermissionsV2({ workspaceId });
  const currentRole = roleQuery.data;

  const statusesQuery = useGetWorkspaceCodeStatuses({ workspaceId });
  const activeByRole = React.useMemo(() => {
    const map = new Map<WorkspaceRole, boolean>();
    (statusesQuery.data ?? []).forEach((status) =>
      map.set(status.role, status.active)
    );
    return map;
  }, [statusesQuery.data]);

  // Default to Editor when the current user can act on it, otherwise their own
  // role (e.g. a Viewer defaults to Viewer — Editor is above them).
  const defaultRole =
    currentRole !== undefined &&
    isRoleAtOrBelow(WorkspaceRole.Editor, currentRole)
      ? WorkspaceRole.Editor
      : currentRole ?? WorkspaceRole.Viewer;
  const [selectedRole, setSelectedRole] =
    React.useState<WorkspaceRole>(defaultRole);

  // Once the role resolves, snap an out-of-range selection back into range.
  React.useEffect(() => {
    if (currentRole === undefined) return;
    const snappedDefault = isRoleAtOrBelow(WorkspaceRole.Editor, currentRole)
      ? WorkspaceRole.Editor
      : currentRole;
    if (!isRoleAtOrBelow(selectedRole, currentRole)) {
      setSelectedRole(snappedDefault);
    }
  }, [currentRole, selectedRole]);

  const onSelect = (event: Event) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = (event as any).detail?.item?.value;
    const role = Number(raw) as WorkspaceRole;
    if (INVITE_ROLE_ORDER.includes(role)) {
      setSelectedRole(role);
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      padding={'default'}
      id="invite-workspace-dialog"
      open={isOpen}
      title={translate('Invite to Library')}
      closeLabel={translate('Cancel')}
    >
      {/* gap-5 = 15px between sections; pt-5 gives the first divider the same
          gap below the Dialog-rendered title. */}
      <div className="flex flex-col gap-5 pt-5 text-base">
        <Divider />
        <div className="leading-8 text-base font-400 text-blue-600">
          {translate('Share this to invite others to join your Library.')}
        </div>
        <Divider />
        <SlDropdown
          className="w-full"
          placement="bottom-start"
          sync="width"
          hoist
        >
          <button
            slot="trigger"
            type="button"
            className="flex w-full cursor-pointer items-center justify-between rounded-2 border-0 bg-white px-3 py-2 font-sans text-base font-400 leading-8 text-blue-900 shadow-border outline-none appearance-none focus:shadow-focus"
          >
            {inviteRoleLabel(selectedRole)}
            <span className="flex items-center text-grey-600">
              <IconChevron />
            </span>
          </button>
          <SlMenu className="invite-role-menu" onSlSelect={onSelect}>
            {INVITE_ROLE_ORDER.map((role) => {
              const selectable =
                currentRole !== undefined && isRoleAtOrBelow(role, currentRole);
              return (
                <SlMenuItem
                  key={role}
                  className="invite-role-menu-item"
                  value={String(role)}
                  disabled={!selectable}
                >
                  <div className="flex flex-col gap-1 whitespace-normal py-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-400 text-blue-900">
                        {inviteRoleLabel(role)}
                      </span>
                      <StatusBadge active={activeByRole.get(role) ?? false} />
                      {/* Locked: a role above the user's own. */}
                      {!selectable ? (
                        <span className="ml-auto flex items-center">
                          <IconLock size="micro" />
                        </span>
                      ) : null}
                    </div>
                    <span className="text-xs font-400 text-grey-900">
                      {inviteRoleDescription(role)}
                    </span>
                  </div>
                </SlMenuItem>
              );
            })}
          </SlMenu>
        </SlDropdown>
        <Divider />
        <InviteRolePanel
          workspaceId={workspaceId}
          workspaceRole={selectedRole}
        />
      </div>
    </Dialog>
  );
}
